import React, { useEffect, useState } from "react";
import TopBar from "./components/Layout/TopBar";
import LeftRail from "./components/Layout/LeftRail";
import BottomBar from "./components/Layout/BottomBar";
import StoryboardEditor from "./components/StoryPlan/StoryboardEditor";
import StoryboardTimelinePlayer from "./components/StoryboardTimelinePlayer";
import defaultImage from "./components/StoryPlan/defaultimage.json";
import {
  DEFAULT_PANEL_DURATION,
  exportStoryboardPDF,
  exportStoryboardProject,
  importStoryboardProject,
  normalizeStoryboard,
} from "./utils/storyboardIO";
import { exportStoryboardVideo, isVideoExportSupported } from "./utils/videoExport";
import styles from "./AppShell.module.css";
//1360 × 762 px

const STORAGE_KEY = "frameflow_storyboard";

const defaultStoryboard = [
  {
    id: "page-1",
    page: 1,
    title: "Capítulo 1: El Inicio",
    description: "El héroe aparece por primera vez en el horizonte.",
    panels: [
      {
        id: "1",
        image: defaultImage.baseImage,
        text: "El héroe observa el horizonte.",
        duration: DEFAULT_PANEL_DURATION,
      },
      {
        id: "2",
        image: defaultImage.baseImage,
        text: "El villano acecha desde las sombras.",
        duration: DEFAULT_PANEL_DURATION,
      },
    ],
  },
];

const loadInitialStoryboard = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const normalized = normalizeStoryboard(JSON.parse(saved));
      if (normalized) return normalized;
    }
  } catch (err) {
    console.warn("No se pudo cargar el storyboard guardado:", err);
  }
  return defaultStoryboard;
};

const App = () => {
  const [storyboard, setStoryboard] = useState(loadInitialStoryboard);
  const [viewMode, setViewMode] = useState("edit"); // "edit" | "player"
  const [saveState, setSaveState] = useState("saved"); // "saved" | "saving"
  const [videoExport, setVideoExport] = useState({ inProgress: false, progress: null });

  // Autoguardado local: persiste el storyboard en cada cambio.
  useEffect(() => {
    setSaveState("saving");
    const timeout = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(storyboard));
      } catch (err) {
        console.warn("No se pudo guardar el storyboard localmente:", err);
      }
      setSaveState("saved");
    }, 250);
    return () => clearTimeout(timeout);
  }, [storyboard]);

  const totalPanels = storyboard.reduce((acc, p) => acc + p.panels.length, 0);

  const handleExportPDF = () => exportStoryboardPDF(storyboard);
  const handleExportProject = () => exportStoryboardProject(storyboard);
  const handleImportProject = (file) => {
    importStoryboardProject(
      file,
      (normalized) => setStoryboard(normalized),
      (message) => alert(message)
    );
  };

  const handleExportVideo = async () => {
    if (videoExport.inProgress) return;
    if (!isVideoExportSupported()) {
      alert(
        "Tu navegador no soporta exportar video (falta MediaRecorder/captureStream). Probá con una versión reciente de Chrome o Firefox."
      );
      return;
    }
    setVideoExport({ inProgress: true, progress: { current: 0, total: totalPanels } });
    try {
      await exportStoryboardVideo(storyboard, {
        onProgress: (current, total) =>
          setVideoExport({ inProgress: true, progress: { current, total } }),
      });
    } catch (err) {
      alert(err.message || "No se pudo exportar el video.");
    } finally {
      setVideoExport({ inProgress: false, progress: null });
    }
  };

  return (
    <div className={styles.shell}>
      <TopBar
        onExportPDF={handleExportPDF}
        onExportProject={handleExportProject}
        onImportProject={handleImportProject}
        onExportVideo={handleExportVideo}
        videoExport={videoExport}
        saveState={saveState}
      />
      <LeftRail viewMode={viewMode} onChangeViewMode={setViewMode} />

      <main className={styles.main}>
        {viewMode === "edit" ? (
          <StoryboardEditor storyboard={storyboard} setStoryboard={setStoryboard} />
        ) : (
          <StoryboardTimelinePlayer storyboard={storyboard} />
        )}
      </main>

      <BottomBar
        pageCount={storyboard.length}
        panelCount={totalPanels}
        saveState={saveState}
      />
    </div>
  );
};

export default App;
